import React, { useState } from "react";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { getPostIngestionDates } from "../../app/api/postIngestionDates";
import PostIngestionDatesPanel from "../../components/Internal/PostIngestionDatesPanel";
import { IPostIngestionDatesResult } from "../../interface/IPostIngestionDates";

type Props = {
  isSidebarOpen: boolean;
};

const DEFAULT_LIMIT = 5;

const PostIngestionDates: React.FC<Props> = ({ isSidebarOpen }) => {
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [data, setData] = useState<IPostIngestionDatesResult | null>(null);

  const handleLoad = async () => {
    setLoading(true);
    setHasFetched(true);

    try {
      const result = await getPostIngestionDates(limit);
      setData(result);
    } catch (error) {
      setData(null);
      const status = (error as AxiosError).response?.status;
      if (status === 403) {
        toast.error("You do not have access to this feature", {
          hideProgressBar: true,
        });
      } else if (status !== 400) {
        toast.error("Failed to fetch post ingestion dates", {
          hideProgressBar: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`${!isSidebarOpen ? "w-[calc(100vw-265px)]" : "w-[calc(100vw-105px)]"} fixed transition-all duration-500 ease-in-out overflow-y-scroll h-[calc(100vh-80px)] ml-1 px-6 py-5`}
    >
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-primary">
            Post Ingestion Dates
          </h1>
          <p className="text-sm text-secondary mt-1">
            See the most recent distinct days when posts were ingested into the
            database. Useful for checking cron health across weekdays.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-light shadow-sm mb-6 p-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <label
              htmlFor="ingestion-limit"
              className="block text-sm text-secondary mb-2"
            >
              Number of distinct days
            </label>
            <input
              id="ingestion-limit"
              type="number"
              min={1}
              max={30}
              value={limit}
              onChange={event =>
                setLimit(Math.min(30, Math.max(1, Number(event.target.value))))
              }
              className="h-11 w-28 rounded-xl border border-light px-3 text-sm text-primary"
            />
          </div>
          <button
            type="button"
            onClick={handleLoad}
            disabled={loading}
            className="h-11 px-8 rounded-xl bg-primary text-white text-sm font-medium hover:opacity-90 disabled:opacity-60"
          >
            {loading ? "Loading..." : "Load"}
          </button>
        </div>

        {loading && <Skeleton height={240} borderRadius={16} />}

        {!loading && !hasFetched && (
          <div className="bg-white rounded-2xl border border-light p-8 text-center shadow-sm">
            <p className="text-primary font-medium">No results yet</p>
            <p className="text-sm text-secondary mt-2">
              Click Load to fetch recent ingestion dates.
            </p>
          </div>
        )}

        {!loading && hasFetched && <PostIngestionDatesPanel data={data} />}
      </div>
    </div>
  );
};

export default PostIngestionDates;
